import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, App as AntApp } from 'antd';
import {
  DatabaseOutlined, CalendarOutlined, ToolOutlined, SafetyCertificateOutlined, LineChartOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { AuthProvider, usePermissions, createAuthAxios } from '@zero/auth-client';
import { LoginForm } from '@zero/auth-admin-ui';
import { RequireAuth, SessionGate, PreAuthToolbar, setSharedAxios, useEnabledModules, readLastModuleUrl } from '@fms/shared';

import { AppLayout }   from './components/AppLayout.jsx';
import { AdminLayout } from './components/AdminLayout.jsx';

import { DashboardPage }      from './pages/admin/DashboardPage.jsx';
import { UsersPage }          from './pages/admin/UsersPage.jsx';
import { RolesPage }          from './pages/admin/RolesPage.jsx';
import { PermissionsPage }    from './pages/admin/PermissionsPage.jsx';
import { PoliciesPage }       from './pages/admin/PoliciesPage.jsx';
import { AuditPage }          from './pages/admin/AuditPage.jsx';
import { AlertsPage }         from './pages/admin/AlertsPage.jsx';
import { ProfilePage }        from './pages/admin/ProfilePage.jsx';
import { SessionsPage }       from './pages/admin/SessionsPage.jsx';
import { BrandingPage }       from './pages/admin/BrandingPage.jsx';
import { SmtpConfigPage }     from './pages/admin/SmtpConfigPage.jsx';
import { GoogleOAuthPage }    from './pages/admin/GoogleOAuthPage.jsx';
import { SecurityConfigPage } from './pages/admin/SecurityConfigPage.jsx';
import { TicketConfigPage }   from './pages/admin/TicketConfigPage.jsx';

const { Title, Paragraph } = Typography;

function ModuleCard({ href, icon, title, desc, disabled = false }) {
  const card = (
    <Card hoverable={!disabled} style={{ height: '100%', opacity: disabled ? 0.5 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <span style={{ fontSize: 28, color: '#1f4e79' }}>{icon}</span>
        <Title level={4} style={{ margin: 0 }}>{title}</Title>
      </div>
      <Paragraph type="secondary" style={{ margin: 0 }}>{desc}</Paragraph>
    </Card>
  );
  if (disabled) return <div style={{ height: '100%', cursor: 'not-allowed' }}>{card}</div>;
  return <a href={href} style={{ display: 'block', height: '100%' }}>{card}</a>;
}

function HomePage() {
  const { t } = useTranslation();
  const enabled = useEnabledModules();   // null khi chưa tải → hiện tất cả
  const allModules = [
    { key: 'master', href: '/fms/master/ships', icon: <DatabaseOutlined />,          title: t('menu.masterData', 'Danh mục'),          desc: 'Đội tàu container Hải An, loại tàu và danh mục dùng chung toàn hệ thống.' },
    { key: 'noon',   href: '/fms/noon/',        icon: <CalendarOutlined />,          title: t('menu.noonReport', 'Báo cáo hàng ngày'), desc: 'Báo cáo Arrival / Departure / Shifting / Noon của tàu và thống kê khai thác, nhiên liệu, phát thải.' },
    { key: 'pms',    href: '/fms/pms/',         icon: <ToolOutlined />,              title: t('menu.pms',        'Bảo trì'),           desc: 'Cây thiết bị, công việc bảo dưỡng định kỳ và báo cáo công việc của đội tàu.' },
    { key: 'dlog',   href: '/fms/dlog/',        icon: <LineChartOutlined />,         title: t('menu.dataLogger', 'Dữ liệu logger'),   desc: 'Số liệu cảm biến thu từ datalogger trên tàu: vị trí, hành trình, vòng quay, lưu lượng kế. Chỉ đọc.' },
    { key: 'admin',  href: '/fms/admin',        icon: <SafetyCertificateOutlined />, title: t('menu.admin',      'Quản trị hệ thống'), desc: 'Quản trị người dùng, vai trò, phân quyền, giao diện thương hiệu và nhật ký hệ thống.' },
  ];
  // admin luôn hiện; các module còn lại theo cấu hình backend. enabled=null (chưa tải) → hiện hết.
  const modules = allModules.filter((m) => m.key === 'admin' || enabled === null || enabled.includes(m.key));
  return (
    <div>
      <Title level={3} style={{ marginTop: 0 }}>FMS HACT — {t('menu.home')}</Title>
      <Paragraph type="secondary">
        Tổng quan toàn hệ thống. Chọn module bên dưới để vào dashboard module tương ứng.
      </Paragraph>
      <Row gutter={[16, 16]}>
        {modules.map((m) => (
          <Col key={m.href} xs={24} sm={12} lg={8} xl={6}>
            <ModuleCard {...m} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

// Trang chủ shell: khi VÀO LẠI web app (phiên trình duyệt mới) → tự mở module + route cuối
// đã lưu; đã redirect 1 lần trong phiên thì thôi (quay lại Trang chủ vẫn xem được launcher).
function ShellHome() {
  const redirected = useRef(false);
  if (!redirected.current) {
    redirected.current = true;
    try {
      if (!sessionStorage.getItem('fms.shellRedirected')) {
        const url = readLastModuleUrl();
        if (url) {
          sessionStorage.setItem('fms.shellRedirected', '1');
          window.location.replace(url);
          return null;   // đang điều hướng — không nháy launcher
        }
      }
    } catch { /* ignore storage disabled */ }
  }
  return <HomePage />;
}

function LoginPage() {
  const { login, loginWithGoogle, context } = usePermissions();
  const navigate = useNavigate();
  useEffect(() => { if (context) navigate('/'); }, [context, navigate]);
  return (
    <>
      <PreAuthToolbar />
      <LoginForm
        title="FMS HACT"
        subtitle="Fleet Management System — Hải An"
        onLogin={({ username, password, totpCode }) => login({ username, password, totpCode })}
        onGoogleLogin={async (idToken) => loginWithGoogle({ idToken })}
      />
    </>
  );
}

// Admin dashboard fallback: user thiếu Auth.Audit.ViewStats → redirect /admin/profile.
function AdminDashboardGuard({ axios }) {
  const { has } = usePermissions();
  if (!has('Auth.Audit.ViewStats')) return <Navigate to="/admin/profile" replace />;
  return <DashboardPage axios={axios} />;
}

export function AppShell() {
  const navigate = useNavigate();
  const handleLogout = useCallback(() => navigate('/login'), [navigate]);
  const axios = useMemo(() => createAuthAxios({ baseURL: '', onLogout: handleLogout }), [handleLogout]);

  // Badge nút hỗ trợ (@fms/shared) + FMSGrid cần axios dùng chung — shell trước đây chưa set.
  useEffect(() => setSharedAxios(axios), [axios]);

  return (
    <AntApp>
      <AuthProvider axios={axios} onAuthError={handleLogout}>
        <SessionGate>
          <Routes>
            {/* Pre-auth: no layout */}
            <Route path="/login" element={<LoginPage />} />

            {/* Business: AppLayout */}
            <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
              <Route path="/"          element={<ShellHome />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
            </Route>

            {/* Admin AuthSystem: AdminLayout, mount /admin/* */}
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index               element={<AdminDashboardGuard axios={axios} />} />
              <Route path="users"        element={<UsersPage          axios={axios} />} />
              <Route path="roles"        element={<RolesPage          axios={axios} />} />
              <Route path="permissions"  element={<PermissionsPage    axios={axios} />} />
              <Route path="policies"     element={<PoliciesPage       axios={axios} />} />
              <Route path="audit"        element={<AuditPage          axios={axios} />} />
              <Route path="alerts"       element={<AlertsPage         axios={axios} />} />
              <Route path="profile"      element={<ProfilePage        axios={axios} />} />
              <Route path="sessions"     element={<SessionsPage       axios={axios} />} />
              <Route path="branding"     element={<BrandingPage       axios={axios} />} />
              <Route path="smtp"         element={<SmtpConfigPage     axios={axios} />} />
              <Route path="google-oauth" element={<GoogleOAuthPage    axios={axios} />} />
              <Route path="security"     element={<SecurityConfigPage axios={axios} />} />
              <Route path="ticket"       element={<TicketConfigPage   axios={axios} />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </SessionGate>
      </AuthProvider>
    </AntApp>
  );
}
